import { addMemberAmenitySlot } from "../../../../Redux/Bookings/BookingsActions";
import { getDashboardData } from "../../../../Redux/Dashboard/DashboardActions";
import validator, { ValidationField } from "../../../../Util/validator";
import toastHelper from "../../../../Util/toastHelper";
import { AppDispatch } from "../../../../Redux/store";
import styles from "./SlotSelectorModal.module.scss";
import Modal from "../../../Common/Modal/Modal";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { useState } from "react";
import moment from "moment";

interface Amenity {
  amenityId: number;
  name: string;
  image: string;
  isActive: boolean;
  lastVisited: string | null;
  amenityTypeName: string;
  isBookableSlot: boolean;
  maxhours: number;
  doors: AmenityDoor[];
  amenitySlots: AmenitySlot[];
}

interface AmenityDoor {
  id: number;
  doorName: string;
  doorId: number;
  panelId: number;
  amenityId: number;
}

interface AmenitySlot {
  amenityId: number;
  endDate: string;
  id: number;
  memberId: number;
  startDate: string;
}

interface Props {
  amenity: Amenity;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function SlotSelectorModal(props: Props) {
  const { amenity, isOpen, setIsOpen } = props;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [errors, setErrors] = useState<{ startTime: string; endTime: string }>({
    startTime: "",
    endTime: "",
  });

  const addSlotHandler = () => {
    let fieldsArray: ValidationField[] = [
      {
        key: "startTime",
        value: startTime,
        type: "text",
      },
      {
        key: "endTime",
        value: endTime,
        type: "text",
      },
    ];
    const { isValid, errors: fieldErrors } = validator(fieldsArray);
    setErrors({ ...errors, ...fieldErrors });
    if (isValid) {
      const today = moment().format("YYYY-MM-DD");
      const start = moment(`${today} ${startTime}`, "YYYY-MM-DD HH:mm");
      const end = moment(`${today} ${endTime}`, "YYYY-MM-DD HH:mm");

      const diffMinutes = end.diff(start, "minutes");
      if (diffMinutes < 60) {
        toastHelper.error("Slot duration must be at least 1 hour");
        return;
      }
      if (diffMinutes > amenity.maxhours * 60) {
        toastHelper.error(
          `Slot duration must be less than ${amenity.maxhours} hours`,
        );
        return;
      }
      const hasOverlap = (amenity?.amenitySlots ?? []).some((slot) => {
        const slotStart = moment(slot.startDate);
        const slotEnd = moment(slot.endDate);
        return start.isBefore(slotEnd) && end.isAfter(slotStart);
      });
      if (hasOverlap) {
        toastHelper.error("Selected slot overlaps with an existing booking");
        return;
      }
      const todayDate = today;
      const successHandler = () => {
        setIsOpen(false);
        setStartTime("");
        setEndTime("");
        dispatch(getDashboardData({ navigate: navigate }));
      };
      dispatch(
        addMemberAmenitySlot({
          amenityId: amenity.amenityId.toString(),
          startDate: moment(
            `${todayDate} ${startTime}`,
            "YYYY-MM-DD HH:mm",
          ).format("YYYY-MM-DDTHH:mm:ss"),
          endDate: moment(`${todayDate} ${endTime}`, "YYYY-MM-DD HH:mm").format(
            "YYYY-MM-DDTHH:mm:ss",
          ),
          successHandler,
        }),
      );
    }
  };

  const calculateDuration = () => {
    if (startTime && endTime) {
      const start = moment(`2000-01-01 ${startTime}`);
      const end = moment(`2000-01-01 ${endTime}`);
      const diffMinutes = end.diff(start, "minutes");

      if (diffMinutes >= 60) {
        const hours = diffMinutes / 60;
        return `${hours.toFixed(1)} hour${hours !== 1 ? "s" : ""}`;
      } else {
        return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""}`;
      }
    }
    return null;
  };

  const todayDate = moment().format("YYYY-MM-DD");
  const todaysBookedSlots = (amenity?.amenitySlots ?? []).filter((slot) =>
    moment(slot.startDate).isSame(todayDate, "day"),
  );

  const overlapsBookedSlot = (
    rangeStart: moment.Moment,
    rangeEnd: moment.Moment,
  ) =>
    todaysBookedSlots.some((slot) => {
      const slotStart = moment(slot.startDate);
      const slotEnd = moment(slot.endDate);
      return rangeStart.isBefore(slotEnd) && rangeEnd.isAfter(slotStart);
    });

  const isStartTimeDisabled = (time: string) => {
    const optionStart = moment(`${todayDate} ${time}`, "YYYY-MM-DD HH:mm");
    const optionEnd = optionStart.clone().add(1, "hour");
    if (optionStart.isSameOrBefore(moment())) return true;
    return overlapsBookedSlot(optionStart, optionEnd);
  };

  const isEndTimeDisabled = (time: string) => {
    if (!startTime) return true;
    const start = moment(`${todayDate} ${startTime}`, "YYYY-MM-DD HH:mm");
    const end = moment(`${todayDate} ${time}`, "YYYY-MM-DD HH:mm");
    if (!end.isAfter(start)) return true;
    return overlapsBookedSlot(start, end);
  };

  const timeOptions = Array.from({ length: 24 }, (_, hour24) => {
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;

    const period = hour24 < 12 ? "AM" : "PM";

    const timeString12Hr = `${hour12.toString().padStart(2, "0")}:00 ${period}`;
    const timeString24Hr = `${hour24.toString().padStart(2, "0")}:00`;

    return { value: timeString24Hr, label: timeString12Hr };
  });

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className={styles.mainContainer}>
        <div className={styles.headerContainer}>
          <div className={styles.headerTitle}>Select a slot</div>
        </div>
        <div className={styles.infoContentContainer}>
          <div className={styles.informationContainer}>
            <p className={styles.information}>
              The slot duration is 1 hour and the maximum duration is{" "}
              {amenity?.maxhours} hours
            </p>
          </div>
          <div className={styles.contentContainer}>
            <div className={styles.doubleInputContainer}>
              <div className={styles.inputContainer}>
                <p className={styles.label}>Start Time</p>
                <select
                  className={styles.input}
                  value={startTime}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selectedTime = moment(value, "HH:mm");
                    const currentTime = moment();

                    if (selectedTime.isSameOrBefore(currentTime)) {
                      toastHelper.error("Start time must be in the future");
                      return;
                    }
                    setStartTime(value);
                    setErrors((prev) => ({ ...prev, startTime: "" }));
                    if (endTime) {
                      const start = moment(value, "HH:mm");
                      const end = moment(endTime, "HH:mm");
                      const rangeStart = moment(
                        `${todayDate} ${value}`,
                        "YYYY-MM-DD HH:mm",
                      );
                      const rangeEnd = moment(
                        `${todayDate} ${endTime}`,
                        "YYYY-MM-DD HH:mm",
                      );
                      if (
                        end.isSameOrBefore(start) ||
                        overlapsBookedSlot(rangeStart, rangeEnd)
                      ) {
                        setEndTime("");
                      }
                    }
                  }}
                >
                  {!startTime && <option value="">Select Time</option>}
                  {timeOptions.map(({ value, label }) => (
                    <option
                      key={value}
                      value={value}
                      disabled={isStartTimeDisabled(value)}
                    >
                      {label}
                    </option>
                  ))}
                </select>

                <p className={styles.error}>{errors.startTime}</p>
              </div>
              <div className={styles.inputContainer}>
                <p className={styles.label}> End Time</p>
                <select
                  className={styles.input}
                  value={endTime}
                  disabled={!startTime}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (startTime) {
                      const start = moment(startTime, "HH:mm");
                      const end = moment(value, "HH:mm");
                      if (end.isSameOrBefore(start)) {
                        toastHelper.error("End time must be after start time");
                        return;
                      }
                      if (isEndTimeDisabled(value)) {
                        toastHelper.error(
                          "Selected slot overlaps with an existing booking",
                        );
                        return;
                      }
                    }
                    setEndTime(value);
                    setErrors((prev) => ({ ...prev, endTime: "" }));
                  }}
                >
                  {!endTime && <option value="">Select Time</option>}
                  {timeOptions.map(({ value, label }) => (
                    <option
                      key={value}
                      value={value}
                      disabled={isEndTimeDisabled(value)}
                    >
                      {label}
                    </option>
                  ))}
                </select>

                <p className={styles.error}>{errors.endTime}</p>
              </div>
            </div>
            {calculateDuration() && (
              <div className={styles.informationContainer}>
                <p className={styles.informationText}>
                  Booking Duration {calculateDuration()}
                </p>
              </div>
            )}
          </div>
          <div className={styles.buttonContainer}>
            <button className={styles.button} onClick={addSlotHandler}>
              Add Slot
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default SlotSelectorModal;
